import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  inputRecipe,
  selectContentRecipe,
  selectItemRecipe,
  selectTriggerRecipe,
  type InputRecipeProps,
} from '../recipes/input.recipe';
import { useFieldControlProps } from './field-context';

type ControlSize = NonNullable<InputRecipeProps['size']>;

export interface SelectOption {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
}

type RootProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Root>;

export interface SelectOptionsProps extends Omit<RootProps, 'children'> {
  options: SelectOption[];
  placeholder?: string;
  size?: ControlSize;
  className?: string;
  id?: string;
  /** Renders the trigger full-width. On by default, matching Input. */
  fullWidth?: boolean;
  children?: never;
}

export interface SelectComposedProps extends RootProps {
  options?: never;
  children: React.ReactNode;
}

/**
 * A union rather than optional props, so passing `options` *and* children is a
 * type error instead of one of them being silently ignored.
 */
export type SelectProps = SelectOptionsProps | SelectComposedProps;

/**
 * Single-select dropdown.
 *
 * Two shapes, and they render identically — the styling lives in one set of
 * recipes shared by both.
 *
 * Pass `options` and it builds the whole control. Most selects are a plain
 * list, and this keeps the call site to one tag:
 *
 *   <Select value={status} onValueChange={setStatus} options={STATUSES} />
 *
 * Pass children and it hands off to the composed parts, for the cases the
 * array cannot express — a trigger that renders an avatar next to the label,
 * say, or options that need their own markup:
 *
 *   <Select value={id} onValueChange={setId}>
 *     <SelectTrigger><SelectValue placeholder="Company" /></SelectTrigger>
 *     <SelectContent>
 *       <SelectItem value="a">Acme Towing</SelectItem>
 *     </SelectContent>
 *   </Select>
 *
 * Prefer `options` where it fits; it is shorter and it cannot be assembled
 * wrong. The composed form exists because ~80 selects in the app were written
 * that way and because some of them genuinely need it.
 *
 * The trigger shares the Input recipe either way, so a Select and an Input on
 * the same form row are exactly the same height and share a border treatment.
 */
export function Select(props: SelectProps) {
  if (props.options === undefined) {
    const { children, ...root } = props as SelectComposedProps;
    return <SelectPrimitive.Root {...root}>{children}</SelectPrimitive.Root>;
  }
  return <SelectWithOptions {...(props as SelectOptionsProps)} />;
}

function SelectWithOptions({
  options,
  placeholder = 'Select…',
  size = 'md',
  className,
  id,
  disabled,
  required,
  fullWidth = true,
  ...props
}: SelectOptionsProps) {
  const fieldProps = useFieldControlProps({ id, disabled, required });

  /*
   * Radix throws if any item has an empty value — it reserves "" for "nothing
   * selected". Option lists here are often built from API data (the impounds
   * screen derives them from the company's configured impound types), so one
   * blank entry would take the whole screen down rather than degrading.
   *
   * Dropping the entry keeps the page up. The warning is so the bad data gets
   * noticed rather than silently swallowed.
   */
  const safeOptions = React.useMemo(() => {
    const usable = options.filter((option) => option.value !== '');
    if (import.meta.env.DEV && usable.length !== options.length) {
      console.warn(
        '[library/Select] Dropped %d option(s) with an empty value — Radix ' +
          'reserves "" for the cleared state. Give every option a real value.',
        options.length - usable.length
      );
    }
    return usable;
  }, [options]);

  return (
    <SelectPrimitive.Root
      disabled={fieldProps.disabled}
      required={fieldProps.required}
      {...props}
    >
      <SelectPrimitive.Trigger
        id={fieldProps.id}
        aria-describedby={fieldProps['aria-describedby']}
        aria-invalid={fieldProps['aria-invalid']}
        className={cn(
          // Same reason as the composed trigger below: the chevron is a flex
          // child, so `pr-9` would reserve its space a second time.
          inputRecipe({ size }),
          selectTriggerRecipe(),
          !fullWidth && 'w-auto',
          className
        )}
      >
        <SelectPrimitive.Value placeholder={placeholder} />
        <SelectPrimitive.Icon asChild>
          <ChevronDown className="size-icon-sm shrink-0 opacity-60" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={4}
          className={selectContentRecipe()}
        >
          <SelectPrimitive.Viewport className="p-1">
            {safeOptions.map((option) => (
              <SelectPrimitive.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={selectItemRecipe()}
              >
                <SelectPrimitive.ItemText>
                  {option.label}
                </SelectPrimitive.ItemText>
                <SelectPrimitive.ItemIndicator className="absolute right-2 flex items-center">
                  <Check className="size-icon-sm" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

/**
 * COMPOSED PARTS
 *
 * For the selects the `options` array cannot express. Styled from the same
 * recipes as the array form, so the two are indistinguishable on screen.
 *
 * `SelectGroup`, `SelectLabel` and `SelectSeparator` are deliberately not
 * here — nothing in the product groups its options yet, and an export nobody
 * uses is how the library ended up with 37 of them. Add them when a screen
 * needs one.
 */
export const SelectValue = SelectPrimitive.Value;

export interface SelectTriggerProps
  extends React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> {
  size?: ControlSize;
  /** Renders full-width. On by default, matching Input. */
  fullWidth?: boolean;
}

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  SelectTriggerProps
>(function SelectTrigger(
  {
    className,
    children,
    size = 'md',
    fullWidth = true,
    id,
    disabled,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
    ...props
  },
  ref
) {
  /*
   * The same wiring the options form does: inside a Field this takes the
   * label's id, points at the message, and turns destructive when the field
   * is invalid. Without it a composed Select showed its message with no
   * highlight at all, and the label pointed at nothing.
   */
  const fieldProps = useFieldControlProps({
    id,
    disabled,
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid,
  });

  return (
    <SelectPrimitive.Trigger
      ref={ref}
      id={fieldProps.id}
      disabled={fieldProps.disabled}
      aria-describedby={fieldProps['aria-describedby']}
      aria-invalid={fieldProps['aria-invalid']}
      className={cn(
        /*
         * Not `hasTrailingIcon` — that adds `pr-9` to clear an icon Input
         * positions absolutely. This chevron is a flex child, so the space
         * would be reserved twice: a 96px trigger was left with 24px for its
         * label, and "Week" rendered as "W…".
         */
        inputRecipe({ size }),
        selectTriggerRecipe(),
        !fullWidth && 'w-auto',
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDown className="size-icon-sm shrink-0 opacity-60" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(function SelectContent(
  { className, children, position = 'popper', sideOffset = 4, ...props },
  ref
) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        ref={ref}
        position={position}
        sideOffset={sideOffset}
        className={cn(selectContentRecipe(), className)}
        {...props}
      >
        <SelectPrimitive.Viewport className="p-1">
          {children}
        </SelectPrimitive.Viewport>
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
});

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(function SelectItem({ className, children, ...props }, ref) {
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(selectItemRecipe(), className)}
      {...props}
    >
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
      <SelectPrimitive.ItemIndicator className="absolute right-2 flex items-center">
        <Check className="size-icon-sm" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
});
